'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function FutureValueCalculator() {
  const t = useTranslations("tools.futureValueCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputs, setInputs] = useState({
    periods: 10,
    startingAmount: 1000,
    interestRate: 6,
    periodicDeposit: 100,
    paymentTiming: 'end' as 'beginning' | 'end'
  });

  const [results, setResults] = useState<{
    futureValue: number;
    startingAmount: number;
    totalDeposits: number;
    totalInterest: number;
    schedule: Array<{
      period: number;
      startingBalance: number;
      deposit: number;
      interest: number;
      endingBalance: number;
    }>;
  } | null>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const formatCurrency = (value: number): string => {
    return '$' + value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const formatPercent = (value: number): string => {
    return value.toFixed(1) + '%';
  };

  const calculateFutureValue = () => {
    const { periods, startingAmount, interestRate, periodicDeposit, paymentTiming } = inputs;
    const r = interestRate / 100;
    const isBeginning = paymentTiming === 'beginning';

    const schedule: Array<{
      period: number;
      startingBalance: number;
      deposit: number;
      interest: number;
      endingBalance: number;
    }> = [];

    let balance = startingAmount;
    let totalDeposits = 0;
    let totalInterest = 0;

    for (let period = 1; period <= periods; period++) {
      const startingBalance = balance;
      const deposit = periodicDeposit;
      let interest = 0;

      if (isBeginning) {
        balance += deposit;
        totalDeposits += deposit;
        interest = balance * r;
        balance += interest;
      } else {
        interest = balance * r;
        balance += interest;
        balance += deposit;
        totalDeposits += deposit;
      }

      totalInterest += interest;

      schedule.push({
        period: period,
        startingBalance: startingBalance,
        deposit: deposit,
        interest: interest,
        endingBalance: balance
      });
    }

    setResults({
      futureValue: balance,
      startingAmount: startingAmount,
      totalDeposits: totalDeposits,
      totalInterest: totalInterest,
      schedule: schedule
    });
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    calculateFutureValue();
  };

  const handleInputChange = (field: string, value: any) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  };

  const createPieChart = (starting: number, deposits: number, interest: number) => {
    const total = starting + deposits + interest;
    const startingPct = (starting / total) * 100;
    const depositsPct = (deposits / total) * 100;
    const interestPct = (interest / total) * 100;

    let currentAngle = -90;
    const radius = 47;
    const cx = 63.5;
    const cy = 55;

    const polarToCartesian = (angle: number) => {
      const rad = (angle * Math.PI) / 180;
      return {
        x: cx + radius * Math.cos(rad),
        y: cy + radius * Math.sin(rad)
      };
    };

    const createSlice = (percentage: number, color: string) => {
      const angle = (percentage / 100) * 360;
      const start = polarToCartesian(currentAngle);
      const end = polarToCartesian(currentAngle + angle);
      const largeArc = angle > 180 ? 1 : 0;

      const path = `M ${cx} ${cy} L ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArc} 1 ${end.x} ${end.y} Z`;
      currentAngle += angle;

      return <path key={color} d={path} fill={color} stroke="white" strokeWidth="1" />;
    };

    return (
      <svg width="280" height="110" xmlns="http://www.w3.org/2000/svg">
        {createSlice(startingPct, '#2b7ddb')}
        {createSlice(depositsPct, '#8bbc21')}
        {createSlice(interestPct, '#910000')}
        <circle cx={cx} cy={cy} r="16.45" fill="white" />
      </svg>
    );
  };

  useEffect(() => {
    calculateFutureValue();
  }, []);

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto' },
    {},
    { maxWidth: '1000px' },
    { maxWidth: '1200px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '1.5rem' },
    {},
    { marginBottom: '2rem' },
    { marginBottom: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const calculatorLayoutStyle = getResponsiveStyle(
    { display: 'grid', gap: '1.5rem', marginTop: '2rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr', gap: '2rem' },
    { gridTemplateColumns: '1fr 1fr', gap: '2rem' }
  );

  const calculatorFormStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const formGroupStyle = getResponsiveStyle(
    { marginBottom: '1.5rem' },
    {},
    {},
    {}
  );

  const labelStyle = {
    display: 'block',
    fontWeight: 600,
    color: '#1e293b',
    marginBottom: '0.5rem',
    fontSize: '0.95rem'
  };

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const
    },
    { padding: '0.75rem' },
    { padding: '0.875rem' },
    { padding: '1rem' }
  );

  const calculateBtnStyle = getResponsiveStyle(
    {
      width: '100%',
      background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginTop: '1rem'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.125rem' },
    { padding: '1.125rem 2.5rem', fontSize: '1.125rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const futureValueDisplayStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      color: 'white',
      borderRadius: '0.75rem',
      textAlign: 'center' as const,
      marginBottom: '1.5rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.25rem', lineHeight: 1.8 }
  );

  const scheduleTableStyle = getResponsiveStyle(
    {
      width: '100%',
      borderCollapse: 'collapse' as const,
      marginTop: '2rem',
      background: 'white',
      borderRadius: '0.75rem',
      overflow: 'hidden',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
    },
    { fontSize: '0.85rem' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' }
  );

  return (
    <div style={containerStyle}>
      {/* Header */}
      <header style={headerStyle}>
        <h1 style={h1Style}>{t("title")}</h1>
        <p style={summaryTextStyle}>{t("subtitle")}</p>
      </header>

      {/* Calculator Tool */}
      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={h2Style}>{t("toolTitle")}</h2>
            <p style={summaryTextStyle}>{t("toolSubtitle")}</p>
          </div>

          <div style={calculatorLayoutStyle}>
            <div style={calculatorFormStyle}>
              <form onSubmit={handleCalculate}>
                <div style={formGroupStyle}>
                  <label htmlFor="periods" style={labelStyle}>{t("periodsLabel")}</label>
                  <input
                    type="number"
                    id="periods"
                    value={inputs.periods}
                    onChange={(e) => handleInputChange('periods', parseFloat(e.target.value))}
                    min="1"
                    max="100"
                    step="1"
                    required
                    style={inputStyle}
                  />
                </div>

                <div style={formGroupStyle}>
                  <label htmlFor="starting-amount" style={labelStyle}>{t("startingAmountLabel")}</label>
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#64748b', fontWeight: 500 }}>$</span>
                    <input
                      type="number"
                      id="starting-amount"
                      value={inputs.startingAmount}
                      onChange={(e) => handleInputChange('startingAmount', parseFloat(e.target.value))}
                      min="0"
                      step="0.01"
                      required
                      style={{ ...inputStyle, paddingLeft: '2rem' }}
                    />
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label htmlFor="interest-rate" style={labelStyle}>{t("interestRateLabel")}</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="number"
                      id="interest-rate"
                      value={inputs.interestRate}
                      onChange={(e) => handleInputChange('interestRate', parseFloat(e.target.value))}
                      min="0"
                      max="100"
                      step="0.01"
                      required
                      style={{ ...inputStyle, paddingRight: '2.5rem' }}
                    />
                    <span style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#64748b', fontWeight: 500 }}>%</span>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label htmlFor="periodic-deposit" style={labelStyle}>{t("periodicDepositLabel")}</label>
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#64748b', fontWeight: 500 }}>$</span>
                    <input
                      type="number"
                      id="periodic-deposit"
                      value={inputs.periodicDeposit}
                      onChange={(e) => handleInputChange('periodicDeposit', parseFloat(e.target.value))}
                      min="0"
                      step="0.01"
                      required
                      style={{ ...inputStyle, paddingLeft: '2rem' }}
                    />
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("paymentTimingLabel")}</label>
                  <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input
                        type="radio"
                        id="payment-beginning"
                        name="payment-timing"
                        value="beginning"
                        checked={inputs.paymentTiming === 'beginning'}
                        onChange={(e) => handleInputChange('paymentTiming', e.target.value)}
                        style={{ width: 'auto', margin: 0 }}
                      />
                      <label htmlFor="payment-beginning" style={{ margin: 0, fontWeight: 500, color: '#475569', cursor: 'pointer' }}>
                        {t("paymentBeginning")}
                      </label>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input
                        type="radio"
                        id="payment-end"
                        name="payment-timing"
                        value="end"
                        checked={inputs.paymentTiming === 'end'}
                        onChange={(e) => handleInputChange('paymentTiming', e.target.value)}
                        style={{ width: 'auto', margin: 0 }}
                      />
                      <label htmlFor="payment-end" style={{ margin: 0, fontWeight: 500, color: '#475569', cursor: 'pointer' }}>
                        {t("paymentEnd")}
                      </label>
                    </div>
                  </div>
                </div>

                <button type="submit" style={calculateBtnStyle}>
                  {t("calculateButton")}
                </button>
              </form>
            </div>

            {results && (
              <div style={resultsStyle}>
                <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem' }}>{t("resultsTitle")}</h3>

                <div style={futureValueDisplayStyle}>
                  <div style={{ fontSize: '0.9rem', opacity: 0.9, marginBottom: '0.5rem' }}>{t("futureValueLabel")}</div>
                  <div style={{ fontSize: screenSize === 'mobile' ? '2rem' : '2.5rem', fontWeight: 700 }}>
                    {formatCurrency(results.futureValue)}
                  </div>
                </div>

                <div style={{ marginBottom: '2rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem 0', borderBottom: '1px solid #e2e8f0' }}>
                    <span style={{ color: '#475569', fontWeight: 500 }}>{t("breakdownStarting")}</span>
                    <span style={{ color: '#0f172a', fontWeight: 600 }}>{formatCurrency(results.startingAmount)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem 0', borderBottom: '1px solid #e2e8f0' }}>
                    <span style={{ color: '#475569', fontWeight: 500 }}>{t("breakdownDeposits")}</span>
                    <span style={{ color: '#0f172a', fontWeight: 600 }}>{formatCurrency(results.totalDeposits)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem 0' }}>
                    <span style={{ color: '#475569', fontWeight: 500 }}>{t("breakdownInterest")}</span>
                    <span style={{ color: '#0f172a', fontWeight: 600 }}>{formatCurrency(results.totalInterest)}</span>
                  </div>
                </div>

                <div style={{ margin: '2rem 0' }}>
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
                    {createPieChart(results.startingAmount, results.totalDeposits, results.totalInterest)}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={{ width: '20px', height: '14px', borderRadius: '3px', background: '#2b7ddb' }}></div>
                      <span style={{ color: '#475569', fontSize: '0.9rem' }}>
                        {t("legendStarting")} ({formatPercent((results.startingAmount / results.futureValue) * 100)})
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={{ width: '20px', height: '14px', borderRadius: '3px', background: '#8bbc21' }}></div>
                      <span style={{ color: '#475569', fontSize: '0.9rem' }}>
                        {t("legendDeposits")} ({formatPercent((results.totalDeposits / results.futureValue) * 100)})
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={{ width: '20px', height: '14px', borderRadius: '3px', background: '#910000' }}></div>
                      <span style={{ color: '#475569', fontSize: '0.9rem' }}>
                        {t("legendInterest")} ({formatPercent((results.totalInterest / results.futureValue) * 100)})
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {results && (
            <table style={scheduleTableStyle}>
              <thead>
                <tr>
                  <th style={{ background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)', color: 'white', padding: screenSize === 'mobile' ? '0.5rem' : '1rem', textAlign: 'left', fontWeight: 600 }}>
                    {t("schedulePeriod")}
                  </th>
                  <th style={{ background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)', color: 'white', padding: screenSize === 'mobile' ? '0.5rem' : '1rem', textAlign: 'left', fontWeight: 600 }}>
                    {t("scheduleStarting")}
                  </th>
                  <th style={{ background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)', color: 'white', padding: screenSize === 'mobile' ? '0.5rem' : '1rem', textAlign: 'left', fontWeight: 600 }}>
                    {t("scheduleDeposit")}
                  </th>
                  <th style={{ background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)', color: 'white', padding: screenSize === 'mobile' ? '0.5rem' : '1rem', textAlign: 'left', fontWeight: 600 }}>
                    {t("scheduleInterest")}
                  </th>
                  <th style={{ background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)', color: 'white', padding: screenSize === 'mobile' ? '0.5rem' : '1rem', textAlign: 'left', fontWeight: 600 }}>
                    {t("scheduleEnding")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {results.schedule.map((row, index) => (
                  <tr key={row.period} style={{ background: index % 2 === 0 ? '#f8fafc' : 'white' }}>
                    <td style={{ padding: screenSize === 'mobile' ? '0.5rem' : '0.75rem 1rem', borderBottom: '1px solid #e2e8f0', color: '#475569' }}>
                      {row.period}
                    </td>
                    <td style={{ padding: screenSize === 'mobile' ? '0.5rem' : '0.75rem 1rem', borderBottom: '1px solid #e2e8f0', color: '#475569' }}>
                      {formatCurrency(row.startingBalance)}
                    </td>
                    <td style={{ padding: screenSize === 'mobile' ? '0.5rem' : '0.75rem 1rem', borderBottom: '1px solid #e2e8f0', color: '#475569' }}>
                      {formatCurrency(row.deposit)}
                    </td>
                    <td style={{ padding: screenSize === 'mobile' ? '0.5rem' : '0.75rem 1rem', borderBottom: '1px solid #e2e8f0', color: '#475569' }}>
                      {formatCurrency(row.interest)}
                    </td>
                    <td style={{ padding: screenSize === 'mobile' ? '0.5rem' : '0.75rem 1rem', borderBottom: '1px solid #e2e8f0', color: '#475569' }}>
                      {formatCurrency(row.endingBalance)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {/* Intro Section */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("introTitle")}</h2>
        <p style={paragraphStyle}>{t("introParagraph1")}</p>
        <p style={paragraphStyle}>{t("introParagraph2")}</p>
        <p style={paragraphStyle}>{t("introParagraph3")}</p>
      </section>

      {/* How to Use */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("howToUseTitle")}</h2>
        <ol style={{ ...paragraphStyle, paddingLeft: '1.5rem' }}>
          <li style={{ marginBottom: '0.75rem' }}>{t("howToUseStep1")}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t("howToUseStep2")}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t("howToUseStep3")}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t("howToUseStep4")}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t("howToUseStep5")}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t("howToUseStep6")}</li>
        </ol>
      </section>

      {/* Key Insights */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("insightsTitle")}</h2>
        <ul style={{ ...paragraphStyle, paddingLeft: '1.5rem' }}>
          <li style={{ marginBottom: '0.75rem' }}>{t("insight1")}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t("insight2")}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t("insight3")}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t("insight4")}</li>
        </ul>
      </section>

      {/* Understanding the Formula */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("formulaTitle")}</h2>
        <p style={paragraphStyle}>{t("formulaParagraph1")}</p>
        <div style={{
          background: 'rgba(37, 99, 235, 0.05)',
          padding: screenSize === 'mobile' ? '1rem' : '1.5rem',
          borderRadius: '0.5rem',
          marginBottom: '1rem',
          fontFamily: 'monospace',
          fontSize: screenSize === 'mobile' ? '0.9rem' : '1rem'
        }}>
          FV = PV × (1 + r)^n + PMT × [((1 + r)^n - 1) / r]
        </div>
        <p style={paragraphStyle}>{t("formulaParagraph2")}</p>
        <ul style={{ ...paragraphStyle, paddingLeft: '1.5rem' }}>
          <li style={{ marginBottom: '0.5rem' }}><strong>FV</strong>: {t("formulaFV")}</li>
          <li style={{ marginBottom: '0.5rem' }}><strong>PV</strong>: {t("formulaPV")}</li>
          <li style={{ marginBottom: '0.5rem' }}><strong>r</strong>: {t("formulaR")}</li>
          <li style={{ marginBottom: '0.5rem' }}><strong>n</strong>: {t("formulaN")}</li>
          <li style={{ marginBottom: '0.5rem' }}><strong>PMT</strong>: {t("formulaPMT")}</li>
        </ul>
      </section>

      {/* Best Practices */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("bestPracticesTitle")}</h2>
        <ul style={{ ...paragraphStyle, paddingLeft: '1.5rem' }}>
          <li style={{ marginBottom: '0.75rem' }}>{t("practice1")}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t("practice2")}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t("practice3")}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t("practice4")}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t("practice5")}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t("practice6")}</li>
        </ul>
      </section>

      {/* FAQ */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("faqTitle")}</h2>

        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginBottom: '0.5rem' }}>
            {t("faq1Question")}
          </h3>
          <p style={paragraphStyle}>{t("faq1Answer")}</p>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginBottom: '0.5rem' }}>
            {t("faq2Question")}
          </h3>
          <p style={paragraphStyle}>{t("faq2Answer")}</p>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginBottom: '0.5rem' }}>
            {t("faq3Question")}
          </h3>
          <p style={paragraphStyle}>{t("faq3Answer")}</p>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginBottom: '0.5rem' }}>
            {t("faq4Question")}
          </h3>
          <p style={paragraphStyle}>{t("faq4Answer")}</p>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginBottom: '0.5rem' }}>
            {t("faq5Question")}
          </h3>
          <p style={paragraphStyle}>{t("faq5Answer")}</p>
        </div>
      </section>

      {/* References */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("referencesTitle")}</h2>
        <ul style={{ ...paragraphStyle, paddingLeft: '1.5rem', listStyle: 'none' }}>
          <li style={{ marginBottom: '0.75rem' }}>
            <a
              href="https://www.shriramfinance.in/article-benefits-of-future-value-calculator"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: colors.primary, textDecoration: 'none', borderBottom: `1px solid ${colors.primary}` }}
            >
              {t("reference1")}
            </a>
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <a
              href="https://www.abacum.ai/glossary/future-value"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: colors.primary, textDecoration: 'none', borderBottom: `1px solid ${colors.primary}` }}
            >
              {t("reference2")}
            </a>
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <a
              href="https://www.rocketmoney.com/learn/investing/future-value"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: colors.primary, textDecoration: 'none', borderBottom: `1px solid ${colors.primary}` }}
            >
              {t("reference3")}
            </a>
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <a
              href="https://www.financialmentor.com/calculator/future-value-calculator"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: colors.primary, textDecoration: 'none', borderBottom: `1px solid ${colors.primary}` }}
            >
              {t("reference4")}
            </a>
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <a
              href="https://corporatefinanceinstitute.com/resources/valuation/future-value-formula/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: colors.primary, textDecoration: 'none', borderBottom: `1px solid ${colors.primary}` }}
            >
              {t("reference5")}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}